import { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import {
  Zap,
  Plus,
  Play,
  Pause,
  Edit,
  Trash2,
  Clock,
  Mail,
  Users,
  TrendingUp,
  Eye,
  ChevronDown,
  ChevronUp,
  MessageSquare
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Switch } from '../ui/switch';
import { marketingAPI, AutomatedEmail, EmailTemplate, WhatsAppTemplate } from '../../lib/marketing-api';
import { toast } from 'sonner';
import { logger } from '../../services/logger.service';

export function AutomatedEmails() {
  const [automations, setAutomations] = useState<AutomatedEmail[]>([]);
  const [emailTemplates, setEmailTemplates] = useState<EmailTemplate[]>([]);
  const [whatsappTemplates, setWhatsappTemplates] = useState<WhatsAppTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingAutomation, setEditingAutomation] = useState<AutomatedEmail | null>(null);
  const [expandedSequences, setExpandedSequences] = useState<Set<string>>(new Set());

  const [formData, setFormData] = useState({
    name: '',
    type: 'drip-sequence' as AutomatedEmail['type'],
    status: 'draft' as AutomatedEmail['status'],
    trigger: {
      event: 'lead-created' as AutomatedEmail['trigger']['event'],
      daysDelay: 0
    },
    sequence: [] as { step: number; channel: 'email' | 'whatsapp'; templateId: string; delayDays: number }[]
  });

  useEffect(() => {
    loadAutomations();
    loadTemplates();
  }, []);

  const loadAutomations = async () => {
    try {
      setIsLoading(true);
      const { emails: data } = await marketingAPI.getAutomatedEmails();
      setAutomations(data);
    } catch (error) {
      logger.error('Failed to load automated emails', error as Error, undefined, 'AutomatedEmails');
      toast.error('Failed to load automated emails');
    } finally {
      setIsLoading(false);
    }
  };

  const loadTemplates = async () => {
    try {
      const { templates: emailData } = await marketingAPI.getEmailTemplates();
      setEmailTemplates(emailData);
      const { templates: whatsappData } = await marketingAPI.getWhatsAppTemplates();
      setWhatsappTemplates(whatsappData);
    } catch (error) {
      logger.error('Failed to load templates', error as Error, undefined, 'AutomatedEmails');
    }
  };

  const handleCreateAutomation = async () => {
    try {
      await marketingAPI.createAutomatedEmail(formData);
      toast.success('Automation created successfully');
      setShowAddDialog(false);
      resetForm();
      loadAutomations();
    } catch (error) {
      logger.error('Failed to create automation', error as Error, undefined, 'AutomatedEmails');
      toast.error('Failed to create automation');
    }
  };

  const handleUpdateAutomation = async () => {
    if (!editingAutomation) return;
    
    try {
      await marketingAPI.updateAutomatedEmail(editingAutomation.id, formData);
      toast.success('Automation updated successfully');
      setEditingAutomation(null);
      resetForm();
      loadAutomations();
    } catch (error) {
      logger.error('Failed to update automation', error as Error, undefined, 'AutomatedEmails');
      toast.error('Failed to update automation');
    }
  };

  const handleToggleAutomation = async (automation: AutomatedEmail) => {
    try {
      const newStatus = automation.status === 'active' ? 'paused' : 'active';
      await marketingAPI.updateAutomatedEmail(automation.id, { status: newStatus });
      toast.success(`Automation ${newStatus === 'active' ? 'activated' : 'paused'}`);
      loadAutomations();
    } catch (error) {
      logger.error('Failed to toggle automation', error as Error, undefined, 'AutomatedEmails');
      toast.error('Failed to toggle automation');
    }
  };

  const openEditDialog = (automation: AutomatedEmail) => {
    setEditingAutomation(automation);
    setFormData({
      name: automation.name,
      type: automation.type,
      status: automation.status,
      trigger: automation.trigger || { event: 'lead-created', daysDelay: 0 },
      sequence: automation.sequence || []
    });
  };

  const resetForm = () => {
    setFormData({
      name: '',
      type: 'drip-sequence',
      status: 'draft',
      trigger: { event: 'lead-created', daysDelay: 0 },
      sequence: []
    });
  };

  const addSequenceStep = () => {
    setFormData({
      ...formData,
      sequence: [
        ...formData.sequence,
        { step: formData.sequence.length + 1, channel: 'email', templateId: '', delayDays: 0 }
      ]
    });
  };

  const removeSequenceStep = (index: number) => {
    const newSequence = formData.sequence.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      sequence: newSequence.map((s, i) => ({ ...s, step: i + 1 }))
    });
  };

  const updateSequenceStep = (index: number, field: string, value: any) => {
    const newSequence = [...formData.sequence];
    newSequence[index] = { ...newSequence[index], [field]: value };
    setFormData({ ...formData, sequence: newSequence });
  };

  const toggleSequenceExpanded = (id: string) => {
    setExpandedSequences(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const getStatusBadge = (status: AutomatedEmail['status']) => {
    const variants: Record<AutomatedEmail['status'], string> = {
      active: 'bg-green-500',
      paused: 'bg-yellow-500',
      draft: 'bg-gray-500'
    };
    
    return (
      <Badge className={variants[status]}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const getTypeBadge = (type: AutomatedEmail['type']) => {
    return type === 'drip-sequence' ? (
      <Badge variant="outline" className="gap-1">
        <Clock className="w-3 h-3" />
        Drip Sequence
      </Badge>
    ) : (
      <Badge variant="outline" className="gap-1">
        <Zap className="w-3 h-3" />
        Trigger-Based
      </Badge>
    );
  };

  const getTemplateName = (templateId: string, channel: 'email' | 'whatsapp') => {
    const template = channel === 'email' ? emailTemplates.find(t => t.id === templateId) : whatsappTemplates.find(t => t.id === templateId);
    return template?.name || 'Unknown Template';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Zap className="w-6 h-6 text-primary" />
            Automated Multi-Channel Campaigns
          </h2>
          <p className="text-muted-foreground mt-1">
            Set up drip sequences and trigger-based automations via Email and WhatsApp
          </p>
        </div>
        <Button onClick={() => setShowAddDialog(true)} className="gap-2" data-testid="marketing-automation-create-button">
          <Plus className="w-4 h-4" />
          Create Automation
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Automations</p>
                <p className="text-2xl font-bold">{automations.length}</p>
              </div>
              <Zap className="w-8 h-8 text-primary/50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Active</p>
                <p className="text-2xl font-bold">
                  {automations.filter(a => a.status === 'active').length}
                </p>
              </div>
              <Play className="w-8 h-8 text-green-500/50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Sent</p>
                <p className="text-2xl font-bold">
                  {automations.reduce((sum, a) => sum + a.stats.sent, 0)}
                </p>
              </div>
              <Mail className="w-8 h-8 text-blue-500/50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Avg. Open Rate</p>
                <p className="text-2xl font-bold">
                  {automations.length > 0
                    ? ((automations.reduce((sum, a) => sum + a.stats.opened, 0) / 
                        automations.reduce((sum, a) => sum + a.stats.sent, 0) || 0) * 100).toFixed(1)
                    : 0}%
                </p>
              </div>
              <Eye className="w-8 h-8 text-purple-500/50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Automations List */}
      <Card>
        <CardHeader>
          <CardTitle>All Automations ({automations.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="py-12 text-center text-muted-foreground">
              Loading automations...
            </div>
          ) : automations.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">
              No automations found. Create your first automated campaign!
            </div>
          ) : (
            <div className="space-y-4">
              {automations.map((automation) => (
                <motion.div
                  key={automation.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="border border-border/50 rounded-lg p-4 hover:border-primary/50 transition-colors"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-lg">{automation.name}</h3>
                        {getStatusBadge(automation.status)}
                        {getTypeBadge(automation.type)}
                      </div>
                      {automation.trigger && (
                        <p className="text-sm text-muted-foreground">
                          Trigger: <span className="font-medium">{automation.trigger.event}</span>
                          {automation.trigger.daysDelay && automation.trigger.daysDelay > 0 && (
                            <> after {automation.trigger.daysDelay} day(s)</>
                          )}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleToggleAutomation(automation)}
                        className={automation.status === 'active' ? 'text-yellow-600' : 'text-green-600'}
                        data-testid="marketing-automation-toggle-button"
                      >
                        {automation.status === 'active' ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEditDialog(automation)}
                        data-testid="marketing-automation-edit-button"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => toggleSequenceExpanded(automation.id)}
                        data-testid="marketing-automation-expand-button"
                      >
                        {expandedSequences.has(automation.id) ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="grid grid-cols-3 gap-4 text-sm mb-3">
                    <div>
                      <div className="flex items-center gap-1 text-muted-foreground">
                        <Mail className="w-4 h-4" />
                        <span>Sent</span>
                      </div>
                      <p className="font-semibold">{automation.stats.sent}</p>
                    </div>
                    <div>
                      <div className="flex items-center gap-1 text-muted-foreground">
                        <Eye className="w-4 h-4" />
                        <span>Opened</span>
                      </div>
                      <p className="font-semibold text-green-600">{automation.stats.opened}</p>
                    </div>
                    <div>
                      <div className="flex items-center gap-1 text-muted-foreground">
                        <TrendingUp className="w-4 h-4" />
                        <span>Clicked</span>
                      </div>
                      <p className="font-semibold text-blue-600">{automation.stats.clicked}</p>
                    </div>
                  </div>

                  {/* Sequence Details */}
                  {expandedSequences.has(automation.id) && automation.sequence && automation.sequence.length > 0 && (
                    <div className="border-t pt-3 mt-3">
                      <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        Multi-Channel Sequence ({automation.sequence.length} steps)
                      </h4>
                      <div className="space-y-2">
                        {automation.sequence.map((step, index) => (
                          <div key={index} className="flex items-center gap-3 bg-muted/30 p-3 rounded-lg">
                            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/20 text-primary font-semibold text-sm">
                              {step.step}
                            </div>
                            {step.channel === 'whatsapp' ? (
                              <MessageSquare className="w-4 h-4 text-green-600" />
                            ) : (
                              <Mail className="w-4 h-4 text-blue-600" />
                            )}
                            <div className="flex-1">
                              <p className="text-sm font-medium">{getTemplateName(step.templateId, step.channel)}</p>
                              <p className="text-xs text-muted-foreground">
                                {step.delayDays === 0 ? 'Immediately' : `After ${step.delayDays} day(s)`}
                              </p>
                            </div>
                            <Badge variant={step.channel === 'whatsapp' ? 'default' : 'outline'} className={step.channel === 'whatsapp' ? 'bg-green-600' : ''}>
                              {step.channel === 'whatsapp' ? 'WhatsApp' : 'Email'}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Automation Dialog */}
      <Dialog open={showAddDialog || editingAutomation !== null} onOpenChange={(open) => {
        if (!open) {
          setShowAddDialog(false);
          setEditingAutomation(null);
          resetForm();
        }
      }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingAutomation ? 'Edit Automation' : 'Create Automated Campaign'}
            </DialogTitle>
            <DialogDescription>
              {editingAutomation ? 'Update automation details' : 'Set up a new email automation'}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="automation-name">Automation Name *</Label>
              <Input
                id="automation-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., New Lead Welcome Series"
                required
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="type">Automation Type</Label>
                <Select
                  value={formData.type}
                  onValueChange={(value: AutomatedEmail['type']) => setFormData({ ...formData, type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="drip-sequence">Drip Sequence</SelectItem>
                    <SelectItem value="trigger-based">Trigger-Based</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value: AutomatedEmail['status']) => setFormData({ ...formData, status: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="paused">Paused</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="border-t pt-4">
              <h4 className="font-semibold mb-3">Trigger Settings</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="trigger-event">Trigger Event</Label>
                  <Select
                    value={formData.trigger.event}
                    onValueChange={(value: any) => setFormData({ 
                      ...formData, 
                      trigger: { ...formData.trigger, event: value }
                    })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="lead-created">Lead Created</SelectItem>
                      <SelectItem value="lead-qualified">Lead Qualified</SelectItem>
                      <SelectItem value="property-viewed">Property Viewed</SelectItem>
                      <SelectItem value="days-after-signup">Days After Signup</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="trigger-delay">Delay (days)</Label>
                  <Input
                    id="trigger-delay"
                    type="number"
                    min="0"
                    value={formData.trigger.daysDelay || 0}
                    onChange={(e) => setFormData({
                      ...formData,
                      trigger: { ...formData.trigger, daysDelay: parseInt(e.target.value) || 0 }
                    })}
                  />
                </div>
              </div>
            </div>

            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold">Email Sequence</h4>
                <Button size="sm" onClick={addSequenceStep} className="gap-2" data-testid="marketing-automation-add-step-button">
                  <Plus className="w-4 h-4" />
                  Add Step
                </Button>
              </div>

              {formData.sequence.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No steps added. Click "Add Step" to create your sequence.
                </div>
              ) : (
                <div className="space-y-3">
                  {formData.sequence.map((step, index) => (
                    <div key={index} className="border border-border/50 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/20 text-primary font-semibold text-sm">
                          {step.step}
                        </div>
                        <div className="flex-1 space-y-3">
                          <div className="space-y-2">
                            <Label>Delivery Channel</Label>
                            <Select
                              value={step.channel}
                              onValueChange={(value: 'email' | 'whatsapp') => {
                                updateSequenceStep(index, 'channel', value);
                                updateSequenceStep(index, 'templateId', ''); // Reset template when channel changes
                              }}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="email">
                                  <div className="flex items-center gap-2">
                                    <Mail className="w-4 h-4" />
                                    Email
                                  </div>
                                </SelectItem>
                                <SelectItem value="whatsapp">
                                  <div className="flex items-center gap-2">
                                    <MessageSquare className="w-4 h-4" />
                                    WhatsApp
                                  </div>
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label>{step.channel === 'email' ? 'Email' : 'WhatsApp'} Template</Label>
                            <Select
                              value={step.templateId}
                              onValueChange={(value) => updateSequenceStep(index, 'templateId', value)}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select template" />
                              </SelectTrigger>
                              <SelectContent>
                                {step.channel === 'email' ? (
                                  emailTemplates.map(template => (
                                    <SelectItem key={template.id} value={template.id}>
                                      {template.name}
                                    </SelectItem>
                                  ))
                                ) : (
                                  whatsappTemplates.map(template => (
                                    <SelectItem key={template.id} value={template.id}>
                                      {template.name}
                                    </SelectItem>
                                  ))
                                )}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label>Delay (days)</Label>
                            <Input
                              type="number"
                              min="0"
                              value={step.delayDays}
                              onChange={(e) => updateSequenceStep(index, 'delayDays', parseInt(e.target.value) || 0)}
                              placeholder="0"
                            />
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeSequenceStep(index)}
                          className="text-destructive"
                          data-testid="marketing-automation-remove-step-button"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowAddDialog(false);
                setEditingAutomation(null);
                resetForm();
              }}
              data-testid="marketing-automation-cancel-button"
            >
              Cancel
            </Button>
            <Button onClick={editingAutomation ? handleUpdateAutomation : handleCreateAutomation} data-testid="marketing-automation-save-button">
              {editingAutomation ? 'Update Automation' : 'Create Automation'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}